package com.neurobs.react

import com.neurobs.errors._
import com.neurobs.http.{RequestCall, RequestProcess}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.useState
import slinky.core.facade.ReactElement

import scala.concurrent.ExecutionContext.Implicits.global
import scala.concurrent.Future

trait FormProcessResult
case object FormProcessResultNone extends FormProcessResult
case class FormProcessResultValue[T](value: T) extends FormProcessResult

abstract class FormProcessor(val result: Future[Result[FormProcessResult]]) {
	def cancellable: Boolean
	def cancel(): Unit
}

case class FormProcessorRequest[T](request: RequestCall[T]) extends FormProcessor(request._2.map(_.map(FormProcessResultValue[T](_)))) {
	override def cancellable: Boolean = true
	override def cancel(): Unit = request._1.abort()
}

case class FormProcessorRequestPost[T, V](request: RequestCall[T])(post: T => Result[V]) extends FormProcessor(request._2.map(_.flatMap( post(_).map(FormProcessResultValue[V](_))))) {
	override def cancellable: Boolean = true
	override def cancel(): Unit = request._1.abort()
}

@react object FormDialog {
	case class State(processor: Option[FormProcessor] = None, error: Option[String] = None)

	case class Props
	(
		title: Option[String] = None,
		description: Option[String] = None,
		submitButtonCaption: String = "Submit",
		submitButtonEnabled: Boolean = true,
		customView: Option[ReactElement] = None,
		emWidth: Option[Double] = None,
		process: () => Result[Either[FormProcessResult, FormProcessor]],
		onFormCancel: Option[() => Unit] = None,
		onSuccess: FormProcessResult => Unit
	)

	val component = FunctionalComponent[Props] { props =>
		val (state, setState) = useState(State())

		def handleResult(res: Result[FormProcessResult]): Unit = {
			res match {
				case Left(e) => setState(state.copy(processor = None, error = Some(e.description)))
				case Right(r) =>
					setState(state.copy(processor = None, error = None))
					props.onSuccess(r)
			}
		}

		def submit(): Unit = {
			props.process() match {
				case Left(e) => setState(state.copy(processor = None, error = Some(e.description)))
				case Right(v) => v match {
					case Left(r) => props.onSuccess(r)
					case Right(p) =>
						p.result.onComplete(t => handleResult(combine(t)))
						setState(state.copy(processor = Some(p), error = None))
				}
			}
		}

		def cancel(): Unit = {
			state.processor match {
				case None => props.onFormCancel.foreach(_())
				case Some(p) => if (p.cancellable) {
					p.cancel()
					setState(state.copy(processor = None, error = None))
				}
			}
		}

		def onClick(b: DialogButton): Unit = {
			b.result match {
				case DialogResult.Cancel => cancel()
				case DialogResult.Ok => submit()
				case _ =>
			}
		}

		val cancelButton =
			if (state.processor.map(_.cancellable).getOrElse(props.onFormCancel.isDefined))
				Some(DialogButton(caption = "Cancel", DialogResult.Cancel))
			else None
		val submitButton = DialogButton(caption = props.submitButtonCaption, result = DialogResult.Ok, disabled = state.processor.isDefined || (!props.submitButtonEnabled))
		val buttons = cancelButton match {
			case Some(b) => Seq(submitButton, b)
			case None => Seq(submitButton)
		}

		Dialog(
			title = props.title,
			description = props.description,
			custom = props.customView,
			error = state.error,
			status = if (state.processor.isDefined) Some("Processing...") else None,
			emWidth = props.emWidth,
			buttons = Right(buttons),
			onClick = b => onClick(b)
		)
	}
}
